import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useOrganization } from "./organization-context";
import apiClient from "../utils/axios";
import { StripeService } from "../services/stripeService";
import { BillingService, BillingStatus } from "../services/billingService";
import { StripeSubscriptionStatus } from "../utils/stripe";

// Centralized subscription data interface
interface SubscriptionData {
  organization_id: string;
  organization_name: string;
  current_facility_count: number;
  active_facility_count: number;
  max_facilities: number;
  billing_cycle: 'Monthly' | 'Annual';
  subscription_status: string;
  is_free_trial: boolean;
  free_trial_expires_at?: string;
  next_billing_date?: string;
  monthly_cost: number;
  annual_cost: number;
  monthly_cost_per_facility: number;
  annual_monthly_cost_per_facility: number;
  setup_fee_per_facility: number;
}

interface SubscriptionContextType {
  // Data
  subscriptionData: SubscriptionData | null;
  stripeStatus: StripeSubscriptionStatus | null;
  billingStatus: BillingStatus | null;
  stripeCustomerInfo: {customer_email: string | null; customer_name: string | null; has_customer: boolean} | null;
  
  // State
  loading: boolean;
  hasActiveSubscription: boolean;
  hasAccess: boolean;
  isInOrg: boolean;
  
  // Cache info
  lastFetchTime: Date | null;
  
  // Actions
  refreshSubscriptionData: (forceRefresh?: boolean) => Promise<void>;
  invalidateCache: () => void;
  forceFullRefresh: () => Promise<void>;
  
  // Backward compatibility
  checkSubscription: () => Promise<void>;
  
  // Derived data helpers
  getCodeIdentifierAccess: () => { accessible: boolean; reason?: string; message?: string };
  getPaymentStatusColor: (status: string) => string;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { organizations, ownedOrganizations, isOrganizationOwner, loading: orgLoading } = useOrganization();
  
  // Centralized state
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeSubscriptionStatus | null>(null);
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [stripeCustomerInfo, setStripeCustomerInfo] = useState<{customer_email: string | null; customer_name: string | null; has_customer: boolean} | null>(null);
  const [isInOrg, setIsInOrg] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null);

  // Cache timeout: 5 minutes for most data, 30 seconds for fresh page loads
  const CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes
  const FRESH_TIMEOUT = 30 * 1000; // 30 seconds

  const refreshSubscriptionData = async (forceRefresh = false) => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    // Check if user is in any organization (not just owned organizations)
    const userIsInOrg = organizations.length > 0;
    setIsInOrg(userIsInOrg);
    
    console.log(`[SubscriptionContext] User organization status:`, {
      totalOrganizations: organizations.length,
      ownedOrganizations: ownedOrganizations.length,
      isOrganizationOwner,
      userIsInOrg,
      organizationIds: organizations.map(org => org.id)
    });

    // For non-organization owners, only fetch basic access status
    if (!isOrganizationOwner || ownedOrganizations.length === 0) {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        
        // Fetch only access status for users without organizations
        const accessResponse = await apiClient.get("/api/access/status", {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (accessResponse.data) {
          setHasActiveSubscription(accessResponse.data.has_active_subscription);
          setHasAccess(accessResponse.data.has_access);
        }
        
        setLastFetchTime(new Date());
        console.log(`[SubscriptionContext] Updated access status for non-owner user, isInOrg: ${userIsInOrg}`);
      } catch (error) {
        console.error("Error fetching access status for non-owner:", error);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Check cache unless forcing refresh
    if (!forceRefresh && lastFetchTime) {
      const timeSinceLastFetch = Date.now() - lastFetchTime.getTime();
      const cacheTimeout = subscriptionData ? CACHE_TIMEOUT : FRESH_TIMEOUT;
      
      if (timeSinceLastFetch < cacheTimeout) {
        console.log(`[SubscriptionContext] Using cached data (${Math.round(timeSinceLastFetch/1000)}s old)`);
        return;
      }
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const organizationId = ownedOrganizations[0].id;
      
      console.log(`[SubscriptionContext] Fetching fresh subscription data for org ${organizationId}`);
      
      // Fetch all subscription-related data in parallel
      const [subscriptionResponse, stripeStatusResponse, billingStatusResponse, customerInfoResponse, accessResponse] = await Promise.allSettled([
        apiClient.get(`/api/organization/${organizationId}/subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        StripeService.getSubscriptionStatus(organizationId, token),
        BillingService.getBillingStatus(organizationId, token),
        StripeService.getCustomerInfo(organizationId, token),
        apiClient.get("/api/access/status", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      
      // Update subscription data
      if (subscriptionResponse.status === 'fulfilled') {
        setSubscriptionData(subscriptionResponse.value.data);
      } else {
        console.error("Error fetching subscription data:", subscriptionResponse.reason);
      }

      // Update Stripe status
      if (stripeStatusResponse.status === 'fulfilled') {
        setStripeStatus(stripeStatusResponse.value);
        setHasActiveSubscription(stripeStatusResponse.value?.has_subscription || false);
        setHasAccess(stripeStatusResponse.value?.has_subscription);
      } else {
        console.error("Error fetching Stripe status:", stripeStatusResponse.reason);
      }

      // Update billing status
      if (billingStatusResponse.status === 'fulfilled') {
        setBillingStatus(billingStatusResponse.value);
      } else {
        console.error("Error fetching billing status:", billingStatusResponse.reason);
      }

      // Update customer info
      if (customerInfoResponse.status === 'fulfilled') {
        setStripeCustomerInfo(customerInfoResponse.value);
      } else {
        console.error("Error fetching customer info:", customerInfoResponse.reason);
      }

      // Update access status from the simpler endpoint
      if (accessResponse.status === 'fulfilled') {
          setHasActiveSubscription(accessResponse.value.data.has_active_subscription);
          setHasAccess(accessResponse.value.data.has_access);
      }

      setLastFetchTime(new Date());
      console.log(`[SubscriptionContext] Successfully updated subscription data`);
      
    } catch (error) {
      console.error("Error in refreshSubscriptionData:", error);
    } finally {
      setLoading(false);
    }
  };

  const invalidateCache = () => {
    console.log(`[SubscriptionContext] Cache invalidated`);
    setLastFetchTime(null);
  };

  const forceFullRefresh = async () => {
    console.log("[SubscriptionContext] Force full refresh triggered");
    
    // Clear all state
    setSubscriptionData(null);
    setStripeStatus(null);
    setBillingStatus(null);
    setStripeCustomerInfo(null);
    setHasActiveSubscription(false);
    
    // Invalidate cache
    invalidateCache();
    
    // Force refresh with new data
    await refreshSubscriptionData(true);
  };

  // Code Identifier access logic
  const getCodeIdentifierAccess = () => {
    // Always allow access if the backend says user has access (includes user-level trials)
    if (hasAccess) {
      return { accessible: true };
    }
    
    // Allow access during organization free trial
    if (subscriptionData?.is_free_trial) {
      return { accessible: true };
    }
    
    // Check billing status for invoice users
    if (billingStatus) {
      // Check invoice billing status
      if (billingStatus.billing_mode === 'Invoice') {
        // Past due (over NET-30) blocks access
        if (billingStatus.payment_status === 'Overdue' && billingStatus.days_overdue && billingStatus.days_overdue > 0) {
          return { 
            accessible: false, 
            reason: 'payment_overdue',
            message: `The Code Identifier is temporarily unavailable due to an overdue payment (${billingStatus.days_overdue} days past due). Please contact your organization administrator to resolve the outstanding invoice.`
          };
        }
        
        // Due but not overdue shows warning but allows access
        if (billingStatus.payment_status === 'Due') {
          return { 
            accessible: true, 
            reason: 'payment_due',
            message: 'Your organization has an outstanding invoice. The Code Identifier will remain available until the payment becomes overdue. Please ensure timely payment to avoid service interruption.'
          };
        }
        
        // Allow access for current payments
        return { accessible: true };
      }
    }
    
    // No subscription means no access
    return { 
      accessible: false, 
      reason: 'subscription_required',
      message: 'An active subscription is required to use the Code Identifier. Please contact your organization administrator to set up billing.'
    };
  };

  // Payment status color helper
  const getPaymentStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'current':
      case 'paid':
        return 'green';
      case 'due':
      case 'pending':
        return 'orange';
      case 'overdue':
      case 'past_due':
        return 'red';
      default:
        return 'default';
    }
  };

  // Initial load and organization changes
  useEffect(() => {
    if (isAuthenticated && !orgLoading) {
      refreshSubscriptionData();
    } else if (!isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated, orgLoading, isOrganizationOwner, organizations.length]);

  // Refresh when organizations change
  useEffect(() => {
    if (isAuthenticated && !orgLoading && organizations.length > 0) {
      // Invalidate cache when organization changes
      invalidateCache();
      refreshSubscriptionData(true);
    }
  }, [organizations.map(org => org.id).join(',')]);

  const contextValue: SubscriptionContextType = {
    subscriptionData,
    stripeStatus,
    billingStatus,
    stripeCustomerInfo,
    loading,
    hasActiveSubscription,
    hasAccess,
    isInOrg,
    lastFetchTime,
    refreshSubscriptionData: (forceRefresh) => refreshSubscriptionData(forceRefresh),
    invalidateCache,
    forceFullRefresh,
    checkSubscription: () => refreshSubscriptionData(false), // Backward compatibility
    getCodeIdentifierAccess,
    getPaymentStatusColor,
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
}; 